// L'interlocuteur, tel que l'app le montre sur la fiche d'un lead et l'admin
// sur celle d'un prospect : sa pastille (photo LinkedIn ou initiales), le
// comité d'approche, les moyens de le joindre, son rôle dans la décision et la
// raison de l'avoir retenu. Un seul dessin pour les deux fronts.
import { ExternalLink, Mail } from "lucide-react";
import type { ContactCandidate } from "@shared/lib/leads-shared";
import { GoogleIcon, LinkedInIcon } from "@shared/components/brand-icons";
import { Avatar, AvatarFallback, AvatarImage } from "@shared/components/ui/avatar";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { Card } from "@shared/components/ui/card";
import { cn } from "@shared/lib/utils";
import { useT } from "@shared/client/locale";

export type DecisionRole = NonNullable<ContactCandidate["decision_role"]>;
export type ContactConfidence = NonNullable<ContactCandidate["confidence"]>;

/** Ce qu'il faut d'un interlocuteur pour le montrer : un contact de lead le fournit tel quel. */
export type ContactView = {
  id: string;
  priority_rank?: number | null;
  contact_name: string;
  contact_role: string | null;
  decision_role: DecisionRole | null;
  confidence: ContactConfidence | null;
  contact_linkedin_url: string | null;
  contact_photo_url: string | null;
  public_professional_email?: string | null;
  contact_reason: string | null;
  outreach_angle?: string | null;
};

/** L'organisation où chercher la personne quand son profil n'a pas été trouvé. */
export type OrganizationQuery = {
  name: string;
  website: string | null;
  contact_name?: string | null;
  contact_role?: string | null;
};

const CONFIDENCE_TONES: Record<ContactConfidence, string> = {
  high: "bg-success",
  medium: "bg-chart-4",
  low: "bg-muted-foreground/50",
};

export type ContactLane = "decision" | "business" | "operations" | "influence";

/** Le rôle d'un interlocuteur, en un mot (its label: `leads.contact.lanes`). */
export function contactLane(contact: Pick<ContactView, "decision_role">): ContactLane {
  if (["economic_buyer", "final_decision_maker", "executive_fallback"].includes(contact.decision_role ?? "")) {
    return "decision";
  }
  if (contact.decision_role === "business_owner") return "business";
  if (contact.decision_role === "operational_owner") return "operations";
  return "influence";
}

function hostnameOf(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** Nom court d'une organisation : sans précision entre parenthèses ni sous-titre (« Groupe X (Normandie — …) » → « Groupe X »). */
function shortOrganizationName(name: string) {
  return name
    .replace(/\s*[([].*$/, "")
    .split(/\s+[—–|:-]\s+/)[0]
    .trim();
}

/** Noms sous lesquels chercher l'organisation : son nom court et, s'il diffère, celui que dit son domaine (nora-imagerie.fr → « nora imagerie »). */
function organizationNames(organization: OrganizationQuery) {
  const names = [shortOrganizationName(organization.name)];
  if (organization.website) {
    const label = hostnameOf(organization.website).split(".")[0].replace(/-/g, " ");
    const known = names.some((name) => name.toLowerCase().replace(/-/g, " ") === label.toLowerCase());
    if (label.length >= 4 && /[a-z]/i.test(label) && !known) names.push(label);
  }
  return names.filter(Boolean);
}

/** Les intitulés d'un dirigeant varient d'une maison à l'autre : on les cherche tous. */
const EXECUTIVE_TITLES = ["directeur général", "directrice générale", "DG", "président", "présidente", "gérant", "CEO"];

function quoted(term: string) {
  return /\s/.test(term) ? `"${term}"` : term;
}

function anyOf(terms: string[]) {
  return terms.length > 1 ? `(${terms.map(quoted).join(" OR ")})` : quoted(terms[0]);
}

type ContactQuery = Pick<ContactView, "contact_name" | "contact_role">;

/**
 * Recherche LinkedIn pré-remplie, quand le profil n'a pas été trouvé directement.
 * LinkedIn exige chaque mot : avec un nom, on n'y cherche que le nom (le réseau
 * classe déjà les homonymes) ; sans nom, l'organisation entre guillemets.
 */
export function linkedinSearchUrl(organization: OrganizationQuery, contact?: ContactQuery) {
  const name = contact?.contact_name || organization.contact_name;
  const keywords = name || organizationNames(organization).map((org) => `"${org}"`).join(" OR ");
  return `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(keywords)}`;
}

/**
 * La même recherche par Google, restreinte aux profils LinkedIn : l'organisation
 * entre guillemets (sous ses deux noms), puis le nom, ou la fonction et ses variantes.
 */
export function googleProfileSearchUrl(organization: OrganizationQuery, contact?: ContactQuery) {
  const name = contact?.contact_name || organization.contact_name;
  const role = contact?.contact_role || organization.contact_role;
  const who = name
    ? `"${name}"`
    : role && /directeur g|directrice g|\bDG\b|CEO|présiden|gérant/i.test(role)
      ? anyOf(EXECUTIVE_TITLES)
      : role
        ? quoted(role)
        : "";
  const query = ["site:linkedin.com/in", anyOf(organizationNames(organization)), who].filter(Boolean).join(" ");
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]!.toUpperCase()).join("") || "?";

/**
 * Un interlocuteur en pastille ronde : la photo de son profil LinkedIn vérifié
 * quand on l'a, ses initiales sinon (et si la photo ne charge plus).
 */
export function ContactAvatar({
  name,
  photo,
  className,
  textClassName = "text-[12px]",
}: {
  name: string;
  photo?: string | null;
  className?: string;
  textClassName?: string;
}) {
  return (
    <Avatar className={cn("size-8 flex-none", className)} title={name}>
      {photo && <AvatarImage alt="" className="object-cover" referrerPolicy="no-referrer" src={photo} />}
      <AvatarFallback className={cn("bg-secondary font-medium text-foreground", textClassName)}>{initials(name)}</AvatarFallback>
    </Avatar>
  );
}

/** Le profil LinkedIn vérifié d'un interlocuteur, en icône à côté de son nom. */
export function ContactLinkedInLink({
  contact,
  className,
}: {
  contact: Pick<ContactView, "contact_name" | "contact_linkedin_url">;
  className?: string;
}) {
  const copy = useT().leads.contact;
  if (!contact.contact_linkedin_url) return null;
  return (
    <Button asChild className={cn("flex-none", className)} size="icon-sm" variant="ghost">
      <a
        aria-label={copy.linkedinProfileOf(contact.contact_name)}
        href={contact.contact_linkedin_url}
        rel="noreferrer"
        target="_blank"
        title={copy.linkedinProfile}
      >
        <LinkedInIcon />
      </a>
    </Button>
  );
}

/**
 * Le comité d'approche : une ligne par personne, sélectionnable. Le profil
 * LinkedIn est un lien à côté du bouton de sélection, pas dedans ; `marker`
 * ajoute un repère à côté du nom (l'interlocuteur retenu, dans l'admin).
 */
export function ContactPicker<T extends ContactView>({
  contacts,
  selectedId,
  onSelect,
  showLane = true,
  marker,
}: {
  contacts: T[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  showLane?: boolean;
  marker?: (contact: T) => React.ReactNode;
}) {
  const copy = useT().leads.contact;
  return (
    <div className="grid gap-2">
      {contacts.map((contact) => (
        <div
          className={cn(
            "flex items-center rounded-lg border transition-colors hover:bg-muted/60",
            selectedId === contact.id && "border-border-strong bg-muted",
          )}
          key={contact.id}
        >
          <button
            aria-pressed={selectedId === contact.id}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 p-2.5 text-left"
            onClick={() => onSelect(contact.id)}
            type="button"
          >
            <ContactAvatar name={contact.contact_name} photo={contact.contact_photo_url} />
            <span className="grid min-w-0 flex-1 gap-0.5">
              <span className="flex min-w-0 items-center gap-1.5">
                <strong className="truncate text-[12.5px] font-semibold">{contact.contact_name}</strong>
                {marker?.(contact)}
              </span>
              <small className="truncate text-[12px] text-muted-foreground">
                {contact.contact_role || copy.roleToConfirm}
              </small>
            </span>
            {showLane && (
              <Badge className="text-[12px]" variant="outline">
                {copy.lanes[contactLane(contact)]}
              </Badge>
            )}
          </button>
          <ContactLinkedInLink className="mr-1.5" contact={contact} />
        </div>
      ))}
    </div>
  );
}

/**
 * LinkedIn et e-mail de la personne. Sans profil vérifié, deux recherches
 * pré-remplies le remplacent.
 */
export function ContactReach({ organization, contact }: { organization: OrganizationQuery; contact: ContactView }) {
  const copy = useT().leads.contact;
  return (
    <div className="flex flex-wrap gap-2">
      {contact.contact_linkedin_url ? (
        <Button asChild size="sm" variant="outline">
          <a href={contact.contact_linkedin_url} rel="noreferrer" target="_blank">
            <LinkedInIcon /> {copy.linkedinProfile} <ExternalLink />
          </a>
        </Button>
      ) : (
        <>
          <Button asChild size="sm" variant="outline">
            <a href={linkedinSearchUrl(organization, contact)} rel="noreferrer" target="_blank">
              <LinkedInIcon /> {copy.searchLinkedin} <ExternalLink />
            </a>
          </Button>
          <Button asChild size="sm" variant="outline">
            <a href={googleProfileSearchUrl(organization, contact)} rel="noreferrer" target="_blank">
              <GoogleIcon /> {copy.searchGoogle} <ExternalLink />
            </a>
          </Button>
        </>
      )}
      {contact.public_professional_email && (
        <Button asChild size="sm" variant="outline">
          <a href={`mailto:${contact.public_professional_email}`}><Mail /> {contact.public_professional_email}</a>
        </Button>
      )}
    </div>
  );
}

/**
 * La fiche d'un interlocuteur : qui il est, comment le joindre, son poids dans
 * la décision et pourquoi lui. `eyebrow` remplace la priorité au-dessus du nom,
 * `actions` se place à droite de l'en-tête, `children` sous la raison.
 */
export function ContactProfileCard({
  organization,
  contact,
  eyebrow,
  actions,
  children,
}: {
  organization: OrganizationQuery;
  contact: ContactView;
  eyebrow?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const copy = useT().leads.contact;
  const rank = contact.priority_rank;
  return (
    <Card className="min-w-0 gap-4 p-5">
      <header className="flex flex-wrap items-center gap-3.5">
        <ContactAvatar className="size-12" name={contact.contact_name} photo={contact.contact_photo_url} textClassName="text-[15px]" />
        <div className="min-w-0 flex-1">
          <span className="text-[12px] font-medium text-muted-foreground">
            {eyebrow ?? (rank === 1 ? copy.firstToContact : rank ? copy.priority(rank) : null)}
          </span>
          <h3 className="m-0 truncate text-[17px] font-semibold tracking-tight">{contact.contact_name}</h3>
          <p className="m-0 truncate text-[12px] text-muted-foreground">{contact.contact_role || copy.roleToConfirm}</p>
        </div>
        {actions && <div className="flex-none max-sm:w-full">{actions}</div>}
      </header>

      {/* Les moyens de joindre la personne, sous son nom : visibles sans défiler. */}
      <ContactReach contact={contact} organization={organization} />

      <div className="flex flex-wrap items-center gap-2">
        <Badge className="gap-1.5 text-[12px]" variant="outline">
          <i className={cn("size-1.5 rounded-full", CONFIDENCE_TONES[contact.confidence || "low"])} />
          {contact.confidence ? copy.confidence[contact.confidence] : copy.confidenceUnknown}
        </Badge>
        {contact.decision_role && (
          <Badge className="text-[12px]" variant="secondary">{copy.decisionRoles[contact.decision_role]}</Badge>
        )}
      </div>

      {contact.contact_reason && (
        <div>
          <strong className="text-[12px] font-semibold">{copy.whyThisPerson}</strong>
          <p className="m-0 mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{contact.contact_reason}</p>
        </div>
      )}
      {contact.outreach_angle && (
        <div>
          <strong className="text-[12px] font-semibold">{copy.approachAngle}</strong>
          <p className="m-0 mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{contact.outreach_angle}</p>
        </div>
      )}

      {children}
    </Card>
  );
}
